/* Filter bar behaviour for the shared snippet `tyt-filter-bar`.
 *
 * Two modes (read from [data-tyt-collection-filter][data-tyt-filter-mode]):
 *   filter (collection page) — Region/Activity options are server-side tag links;
 *     the search box live-filters the product cards rendered on the page, and on
 *     load it applies a ?q= param (so a search routed from the homepage lands
 *     pre-filtered).
 *   route (homepage grid) — Region/Activity options are links to the collection;
 *     the search box navigates to the collection with ?q= on Enter.
 *
 * On the collection page the grid (`[data-tyt-product-grid]`) carries EVERY
 * trail, so this file also owns:
 *   - the "Show all N trails" reveal (only the first N cards display at rest);
 *   - background loading of any further pages if the catalogue passes the
 *     per-page cap, so search never reports a false "no results";
 *   - search matching: a term must match the START of a word (decided by Jay,
 *     2026-08-24), against what the trail is CALLED first and its description
 *     only as a fallback (Jay, 2026-09-23 — see cardMatches).
 *
 * The route mode (homepage teaser) is deliberately untouched by all of that.
 */
(function () {
  'use strict';

  /* Word separators: whitespace, dashes, slashes, punctuation. Anything that is
   * not a separator counts as part of a word, so accented letters (Fimmvörðuháls)
   * stay inside their word instead of splitting it. */
  var SEP = /[\s \-‐-―_/\\.,;:!?'"‘’“”()\[\]{}·•|+&@#*~^%$=<>]+/;

  function startsWith(s, p) { return s.lastIndexOf(p, 0) === 0; }

  /* Accents are folded on both sides, so "muxia" finds "Muxía" and "frances"
   * finds "Francés" — customers rarely type the accent. The same fold() as
   * tyt-route-hint.js; scripts/test-collection-search-matching.js fails if the
   * two ever differ. */
  function fold(s) {
    s = String(s || '').toLowerCase();
    return s.normalize ? s.normalize('NFD').replace(/[̀-ͯ]/g, '') : s;
  }

  function splitWords(s) {
    return fold(s).split(SEP).filter(function (w) { return w.length > 0; });
  }

  /* Card haystacks are split once and cached — descriptions make them long and
   * the search runs on every keystroke. Two separate caches, because the two
   * tiers are searched separately (see cardMatches). */
  function wordsOf(card) {
    if (!card._tytWords) card._tytWords = splitWords(card.getAttribute('data-search-text'));
    return card._tytWords;
  }

  function descWordsOf(card) {
    if (!card._tytDescWords) card._tytDescWords = splitWords(card.getAttribute('data-search-desc'));
    return card._tytDescWords;
  }

  /* Every typed term must match the beginning of some word on the card.
   * "rim"  → Rim To Rim, Tahoe Rim   (NOT Camino Primitivo)
   * "camino del norte" → all three terms found, so the raw multi-word route tag hits.
   *
   * WHICH TEXT gets searched is two-tier (Jay, 2026-09-23). Called with
   * withDesc falsy it reads only what the trail is CALLED — name, title, raw
   * tags, region, country. Called with withDesc true it also reads the
   * description. The grid runs tier 1 alone, and only drops to tier 2 when
   * tier 1 matches nothing.
   *
   * Why it cannot be one blob: a term only has to START some word, and the word
   * "to" appears in 222 of our 223 descriptions ("order your poster now to
   * relive…"). With descriptions in the same tier, "coast to coast" matched
   * every card "coast" matched — 21 either way, the bug this fixes. Measured
   * against the live catalogue, 2026-09-23. Tier 1 alone gives "coast" 10 and
   * "coast to coast" 1, and drops a "rim" false positive (West Coast Trail).
   *
   * Tier 2 searches name AND description together, not the description alone,
   * so a mixed query ("laugavegur iceland") still works when only one of its
   * words is in the name. */
  function cardMatches(card, terms, withDesc) {
    var words = wordsOf(card);
    var desc = withDesc ? descWordsOf(card) : null;
    for (var i = 0; i < terms.length; i++) {
      var hit = false;
      for (var j = 0; j < words.length; j++) {
        if (startsWith(words[j], terms[i])) { hit = true; break; }
      }
      if (!hit && desc) {
        for (var k = 0; k < desc.length; k++) {
          if (startsWith(desc[k], terms[i])) { hit = true; break; }
        }
      }
      if (!hit) return false;
    }
    return true;
  }

  // ── the collection grid ────────────────────────────────────────────────────
  function Grid(el) {
    this.el = el;
    this.initial = parseInt(el.getAttribute('data-initial-visible'), 10) || 0;
    this.total = parseInt(el.getAttribute('data-total-count'), 10) || 0;
    /* Batch reveal (Maud, 2026-08-25): each "Show more" click adds another
     * batch rather than dumping all ~195 at once. `shown` is how many are
     * revealed right now; it only grows. */
    this.shownLimit = this.initial;
    this.terms = [];
    this.query = '';
    this.loading = false;

    this.revealWrap = document.querySelector('[data-tyt-grid-reveal]');
    this.revealBtn = document.querySelector('[data-tyt-grid-reveal-btn]');
    this.noResults = document.querySelector('[data-tyt-cfilter-noresults]');
    this.countEl = document.querySelector('[data-tyt-collection-count]');
    this.countBase = this.countEl ? this.countEl.textContent : '';
    /* Shown only when a search fell through to descriptions. Its text in the
     * snippet is the template; [term] is the typed query. */
    this.fallbackEl = document.querySelector('[data-tyt-cfilter-fallback]');
    this.fallbackTpl = this.fallbackEl ? this.fallbackEl.textContent : '';

    var self = this;
    if (this.revealBtn) {
      this.revealBtn.addEventListener('click', function () {
        var batch = self.initial > 0 ? self.initial : 24;
        self.shownLimit += batch;
        self.render();
      });
    }

    this.render();
    this.loadRest();
  }

  Grid.prototype.cards = function () {
    return this.el.querySelectorAll('[data-tyt-product-card]');
  };

  Grid.prototype.setSearch = function (q) {
    this.query = (q || '').trim();
    this.terms = splitWords(q);
    if (this.terms.length > 0 && this.allUrl && !this.allRequested) this.loadAll();
    this.render();
  };

  /* A card fetched from the full Trails page by loadAll. */
  function isExtra(card) { return card.getAttribute('data-tyt-extra') !== null; }

  /* Region pages search EVERY trail (Maud, 2026-10-28, one-list 7.2 option 2).
   * A region page only holds its own region's cards, because the region filter
   * is a server-side tag link. On the first keystroke the full Trails page
   * (data-tyt-all-url on the filter bar) is fetched once and its other cards are
   * appended, marked data-tyt-extra. They only ever show while a search is
   * typed; with the box empty the page is the region again. If the fetch fails
   * the search simply stays inside the region. */
  Grid.prototype.loadAll = function () {
    var canFetch = typeof window.fetch === 'function' && typeof window.DOMParser === 'function';
    this.allRequested = true;
    if (!canFetch) return;
    this.loadingAll = true;

    var self = this;
    var have = {};
    var own = this.cards();
    for (var i = 0; i < own.length; i++) have[own[i].getAttribute('href')] = true;

    function fetchPage(url) {
      window.fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var other = doc.querySelector('[data-tyt-product-grid]');
          if (!other) throw new Error('no product grid at ' + url);
          var more = other.querySelectorAll('[data-tyt-product-card]');
          for (var j = 0; j < more.length; j++) {
            var href = more[j].getAttribute('href');
            if (have[href]) continue;
            have[href] = true;
            var card = document.importNode(more[j], true);
            card.setAttribute('data-tyt-extra', '');
            card.style.display = 'none';
            self.el.appendChild(card);
          }
          var next = (other.getAttribute('data-next-page-url') || '').trim();
          if (next) { fetchPage(next); return; }
          self.wide = true;
          self.loadingAll = false;
          self.render();
        })
        .catch(function (err) {
          if (window.console && console.warn) console.warn('[tyt] could not load all trails for search:', err);
          self.loadingAll = false;
          self.render();
        });
    }
    fetchPage(this.allUrl);
  };

  Grid.prototype.render = function () {
    var cards = this.cards();
    var searching = this.terms.length > 0;
    var busy = this.loading || this.loadingAll;
    var shown = 0;
    var matched = 0;
    var hits = [];
    var i;
    var ownCount = 0;
    for (i = 0; i < cards.length; i++) if (!isExtra(cards[i])) ownCount++;

    /* Pass 1 — names only. This is the answer nearly every search gets. */
    if (searching) {
      for (i = 0; i < cards.length; i++) {
        hits[i] = cardMatches(cards[i], this.terms, false);
        if (hits[i]) matched++;
      }
    }

    /* Pass 2 — descriptions, but only when no trail is CALLED this, so nothing
     * that used to be findable stops being findable. Held back while later
     * pages are still arriving, for the same reason "no results" is: the name
     * match may be on a page that has not landed yet. */
    var fellBack = false;
    if (searching && matched === 0 && !busy) {
      for (i = 0; i < cards.length; i++) {
        hits[i] = cardMatches(cards[i], this.terms, true);
        if (hits[i]) matched++;
      }
      fellBack = matched > 0;
    }

    /* Route hint ("Customizable | + Muxía route") — names the route a search
     * matched on a Customiser card, and clears it when the search is cleared.
     * The logic lives in tyt-route-hint.js so the search results page shares
     * it; a missing file just means no hint. */
    var hint = window.TytRouteHint;
    for (i = 0; i < cards.length; i++) {
      if (hint) hint.apply(cards[i], searching ? this.query : '');
    }

    for (i = 0; i < cards.length; i++) {
      /* A fetched card is never part of the resting region list. */
      var ok = searching ? hits[i] : !isExtra(cards[i]);
      /* While searching, every match shows — including ones past the current
       * batch, so a search is never silently truncated by the reveal state. */
      var visible = ok && (searching || shown < this.shownLimit);
      if (visible) shown++;
      cards[i].style.display = visible ? '' : 'none';
    }

    if (this.fallbackEl) {
      this.fallbackEl.hidden = !fellBack;
      if (fellBack) {
        this.fallbackEl.textContent = this.fallbackTpl.replace('[term]', this.query);
      }
    }

    if (this.revealWrap) {
      /* The button stays until every card is out, so it can be clicked
       * repeatedly. Hidden while searching (a search shows all its matches). */
      var hideBtn = searching || this.shownLimit <= 0 || ownCount <= this.shownLimit;
      this.revealWrap.hidden = hideBtn;
      if (!hideBtn && this.revealBtn) {
        /* [count] = total trails, [remaining] = still hidden. Both optional —
         * the default label carries no number. Liquid can't pass "{count}"
         * through a {{ ... }} tag, hence square brackets; {count} still works
         * if someone types it out of habit. */
        var tpl = this.revealBtn.getAttribute('data-label') || 'Show more';
        var n = Math.max(this.total, ownCount);
        var left = Math.max(0, ownCount - this.shownLimit);
        this.revealBtn.textContent = tpl
          .replace('[count]', n).replace('{count}', n)
          .replace('[remaining]', left).replace('{remaining}', left);
      }
    }

    /* Never say "no results" while later pages are still arriving. */
    if (this.noResults) {
      this.noResults.hidden = !(searching && matched === 0 && !busy);
    }

    if (this.countEl) {
      if (searching) {
        this.countEl.textContent = matched + (matched === 1 ? ' trail matches' : ' trails match') +
          (this.wide ? ' across all trails' : '');
      } else {
        this.countEl.textContent = this.countBase;
      }
    }

    /* Layout hook. tyt-grid-flow.js registers itself here so the staggered grid
     * can re-flow after the visible set changes - the search, each "Show more"
     * batch, and every background page appended below. Deliberately a plain
     * callback and NOT a CustomEvent: the node test harness builds a stub DOM
     * with no dispatchEvent and no CustomEvent, and those tests are worth more
     * than the tidiness of an event. Nothing listening = one typeof check. */
    if (typeof this.el._tytOnRender === 'function') this.el._tytOnRender(this);
  };

  /* Fetch page 2, 3, … in the background and append their cards. Only ever runs
   * once the catalogue passes the per-page setting (250 by default), so today
   * this is a no-op — lower "Products per page" in the editor to exercise it. */
  Grid.prototype.loadRest = function () {
    var next = (this.el.getAttribute('data-next-page-url') || '').trim();
    var canFetch = typeof window.fetch === 'function' && typeof window.DOMParser === 'function';

    if (!next || !canFetch) {
      this.loading = false;
      this.render();
      return;
    }

    this.loading = true;
    this.render();

    var self = this;
    window.fetch(next, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextGrid = doc.querySelector('[data-tyt-product-grid]');
        if (!nextGrid) throw new Error('no product grid at ' + next);

        var more = nextGrid.querySelectorAll('[data-tyt-product-card]');
        for (var i = 0; i < more.length; i++) {
          self.el.appendChild(document.importNode(more[i], true));
        }
        self.el.setAttribute('data-next-page-url', nextGrid.getAttribute('data-next-page-url') || '');
        self.render();
        self.loadRest();
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn('[tyt] could not load more trails:', err);
        self.el.setAttribute('data-next-page-url', '');
        self.loading = false;
        self.render();
      });
  };

  function init() {
    var bar = document.querySelector('[data-tyt-collection-filter]');
    if (!bar) return;
    var mode = bar.getAttribute('data-tyt-filter-mode') || 'filter';
    var base = bar.getAttribute('data-tyt-filter-base') || '';

    // ── dropdown open / close ────────────────────────────────────────────────
    var dds = Array.prototype.slice.call(bar.querySelectorAll('[data-tyt-cdd]'));

    function closeAll(except) {
      dds.forEach(function (dd) {
        if (dd === except) return;
        var m = dd.querySelector('[data-tyt-cdd-menu]');
        var t = dd.querySelector('[data-tyt-cdd-toggle]');
        if (m) m.hidden = true;
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    dds.forEach(function (dd) {
      var toggle = dd.querySelector('[data-tyt-cdd-toggle]');
      var menu = dd.querySelector('[data-tyt-cdd-menu]');
      if (!toggle || !menu) return;
      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = menu.hidden;
        closeAll(dd);
        menu.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      });
      menu.addEventListener('click', function (e) { e.stopPropagation(); });
    });
    document.addEventListener('click', function () { closeAll(null); });
    /* Touch: iOS Safari does not reliably fire a click on `document` for a tap
       that lands on a non-interactive element, so the line above can silently
       fail on a phone and leave the menu stuck open over the results. A
       pointerdown always fires.
       Guarded to non-mouse pointers, so with a mouse NOTHING changes: the click
       handler above still does the closing, on release, exactly as before.
       Containment-checked rather than trusting stopPropagation, because
       pointerdown runs BEFORE click — the toggle's own stopPropagation has not
       happened yet, so a bare close here would be undone (and the menu
       re-opened) by the click that follows. */
    document.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      if (e.target && e.target.closest && e.target.closest('[data-tyt-cdd]')) return;
      closeAll(null);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(null); });

    // ── the grid (collection page only; set up even if the search box is off) ─
    var gridEl = document.querySelector('[data-tyt-product-grid]');
    if (gridEl && !gridEl._tytGrid) gridEl._tytGrid = new Grid(gridEl);
    var grid = gridEl ? gridEl._tytGrid : null;
    /* Region pages: search every trail, not just this region (see loadAll). */
    if (grid && bar.getAttribute('data-tyt-all-url')) grid.allUrl = bar.getAttribute('data-tyt-all-url');

    // ── search ───────────────────────────────────────────────────────────────
    var search = bar.querySelector('[data-tyt-cfilter-search]');
    if (!search) return;

    if (mode === 'route') {
      // homepage: navigate to the collection, carrying the query
      var go = function () {
        var q = search.value.trim();
        var url = base || '/collections/all';
        if (q) url += (url.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(q);
        window.location.href = url;
      };
      search.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); go(); }
      });
      return;
    }

    if (!grid) return;

    /* Changing the sort reloads the page, so carry the query with it — otherwise
     * comparing results across sort orders silently drops the search. */
    function syncSortQuery(q) {
      var form = bar.querySelector('.tyt-cfilter-sort');
      if (!form) return;
      var hidden = form.querySelector('input[name="q"]');
      q = (q || '').trim();
      if (!q) {
        if (hidden) hidden.parentNode.removeChild(hidden);
        return;
      }
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'q';
        form.appendChild(hidden);
      }
      hidden.value = q;
    }

    var t;
    search.addEventListener('input', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        grid.setSearch(search.value);
        syncSortQuery(search.value);
      }, 120);
    });
    search.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });

    // apply a ?q= routed in from the homepage (or carried through a sort change)
    try {
      var q0 = new URLSearchParams(window.location.search).get('q');
      if (q0) {
        search.value = q0;
        grid.setSearch(q0);
        syncSortQuery(q0);
      }
    } catch (e) { /* URLSearchParams unsupported — ignore */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
